import axios from 'axios';
import { storeAccessor } from './storeAccessor';
import { getJourneyId, JOURNEY_ID_HEADER } from '../utils/journeyId';

/**
 * Axios instance with interceptors for:
 * - Project context injection (X-Project-Id header)
 * - Journey id for path tracing (X-Journey-Id header)
 * - Session management (redirect on expiration)
 */

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

// UUID validation regex (strict format)
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Validates if a string is a valid UUID v4
 * @param {string} uuid - The string to validate
 * @returns {boolean}
 */
const isValidUUID = (uuid) => {
  return uuid && typeof uuid === 'string' && UUID_REGEX.test(uuid);
};

/**
 * Extracts project ID from URL pathname
 * @param {string} pathname - The URL pathname
 * @returns {string|null} - Valid project ID or null
 */
const extractProjectIdFromURL = (pathname) => {
  try {
    const match = pathname.match(/\/projects\/([a-f0-9-]+)/i);
    if (match && match[1] && isValidUUID(match[1])) {
      return match[1];
    }
  } catch (error) {
    console.error('Error extracting project ID from URL:', error);
  }
  return null;
};

/**
 * Retrieves project ID from Redux store
 * @returns {string|null} - Valid project ID or null
 */
const getProjectIdFromStore = () => {
  try {
    const state = storeAccessor.getState();
    const projectId = state?.projects?.selectedProject?.id;
    if (projectId && isValidUUID(projectId)) {
      return projectId;
    }
  } catch (error) {
    console.error('Error accessing project ID from store:', error);
  }
  return null;
};

const getOrganizationIdFromStore = () => {
  try {
    const state = storeAccessor.getState();
    const organizationId = state?.auth?.user?.org_id;
    if (organizationId && isValidUUID(organizationId)) {
      return organizationId;
    }
  } catch (error) {
    console.error('Error accessing organization ID from store:', error);
  }
  return null;
};

/**
 * Gets CSRF token from cookie
 * @returns {string|null} - CSRF token or null
 */
const getCSRFToken = () => {
  const name = 'XSRF-TOKEN';
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }
  return null;
};

// Request interceptor: Inject project context from Redux store or URL
axiosInstance.interceptors.request.use(
  (config) => {
    try {
      config.headers = config.headers || {};

      let projectId = extractProjectIdFromURL(window.location.pathname);      
      if (!projectId) {
        projectId = getProjectIdFromStore();
      }
      
      if (projectId) {
        config.headers['x-project-id'] = projectId;
      }

      let organizationId = getOrganizationIdFromStore();
      if (organizationId) {
        config.headers['x-organization-id'] = organizationId;
      }

      config.headers[JOURNEY_ID_HEADER] = getJourneyId();
      
      // Add CSRF token to all non-GET requests
      if (config.method && !['get', 'head', 'options'].includes(config.method.toLowerCase())) {
        const csrfToken = getCSRFToken();
        if (csrfToken) {
          config.headers['X-CSRF-Token'] = csrfToken;
        } else {
          console.warn('[Axios] CSRF token not found in cookies');
        }
      }
    } catch (error) {
      // Log error but don't block the request
      console.error('[Axios] Error in request interceptor:', error);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: Handle session expiration and trial-limit 403 messages
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const path = window.location.pathname;
    const isPublicPage = path === '/login' || path === '/signup';
    if (
      error.response?.data?.sessionExpired &&
      !isPublicPage
    ) {
      window.location.href = '/logout';
    }
    const isConsentRequired =
      error.response?.status === 403 &&
      error.response?.data?.code === 'CONSENT_REQUIRED';

    if (error.response?.status === 403 && error.response?.data?.message && !isConsentRequired) {
      error.trialLimitMessage = error.response.data.message;
    }
    if (isConsentRequired) {
      storeAccessor.dispatch({ type: 'auth/setConsentModalRequired', payload: true });
    }
    // WAF blocks return 403 without CORS headers, so axios surfaces them as ERR_NETWORK
    // with no response. Surface an actionable message instead of the generic "Network Error".
    if (error?.code === 'ERR_NETWORK' && !error.response) {
      error.message = 'Our security filter blocked this request.';
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;